import React from 'react';
import Markdown from '../CompletionChatbot/Markdown';

import { NodeSidebarTab } from './NodeSidebarTabs';
import { Alert, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Label } from '@common/Label';
import { NodeSidebarCodebox } from './NodeSidebarContainer';
import { Code } from '@common/Code';
import { Command } from '@store/slices';
import { useNodeData } from '@common/hooks/useNodeData';
import { Loader } from '@common/hooks/useLoader';
import { Card } from '@common/Card';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions, State, tabActions } from '@store/index';
import { SubText } from '@common/SubText/SubText';

interface Props {
  command: Command;
  loading: boolean;
  unknownData?: boolean;
  tooltip?: string;
}

export const NodeExecuteData: React.FC<Props> = ({ command, loading, unknownData = false, tooltip }) => {
  const dispatch = useDispatch();

  const { getOutputData } = useNodeData();

  const dataAnchor = command.config.anchors.find((anchor) => anchor.variant === 'data' && anchor.type === 'target');
  const output = dataAnchor ? getOutputData(command.node.id, dataAnchor) : 'null';

  const dataPrefixes = useSelector((state: State) => state.tabs.active?.flow?.data_prefixes) || {};
  const prefix = dataPrefixes?.[command.node.id];

  const changeDataPrefix = () => {
    if (prefix) return dispatch(tabActions.deleteDataPrefix({ node_id: command.node.id }));
    dispatch(flowActions.setShowDataPrefix(command.node.id));
  };

  if (loading) {
    return (
      <NodeSidebarTab>
        <Stack width='100%' pt={1} alignItems='center'>
          <Loader />
        </Stack>
      </NodeSidebarTab>
    );
  }

  if (unknownData && !command.output.dynamic) {
    return (
      <NodeSidebarTab>
        <Alert severity='warning'>
          This node has not been executed yet. The data is currently unknown. Run this node to use the output data in
          future nodes.
        </Alert>
      </NodeSidebarTab>
    );
  }

  return (
    <NodeSidebarTab>
      <Stack spacing={1}>
        <Label
          title='Global Prefix'
          tooltip={`Enable this feature to make the data for this node globally available to all other nodes in the workflow.`}
        />
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={Boolean(prefix)}
          exclusive
          onChange={() => changeDataPrefix()}
          fullWidth
        >
          <ToggleButton size='small' value={true}>
            Enabled
          </ToggleButton>
          <ToggleButton size='small' value={false}>
            Disabled
          </ToggleButton>
        </ToggleButtonGroup>
        {prefix && (
          <SubText>
            Prefix Name: <span style={{ color: '#00E3AA' }}>{prefix}</span>
          </SubText>
        )}
      </Stack>
      <Stack spacing={1}>
        <Label
          title='Output Data'
          tooltip={
            tooltip ||
            'This is the schema of the data this node outputs. If the node has been executed at least once, real values from the last execution will be used instead of expected types.'
          }
        />
        <NodeSidebarCodebox>
          <Code language='json' code={output} />
        </NodeSidebarCodebox>
      </Stack>
      {output?.llm?.completion?.answer && output?.llm?.completion?.answer !== 'answer' && (
        <Stack spacing={1}>
          <Label title='Last Completion' tooltip='This is the last answer generated by the LLM.' />
          <Card body={<Markdown content={output.llm.completion.answer} />} />
        </Stack>
      )}
    </NodeSidebarTab>
  );
};

export const NodeData: React.FC<{ command: Command; terminal?: boolean }> = ({ command, terminal = false }) => {
  const dispatch = useDispatch();

  const { getOutputData, getInputData } = useNodeData();

  const dataPrefixes = useSelector((state: State) => state.tabs.active?.flow?.data_prefixes) || {};
  const prefix = dataPrefixes?.[command.node.id];

  const dataAnchor = command.config.anchors.find((anchor) => anchor.variant === 'data' && anchor.type === 'source');
  const output = terminal
    ? getInputData(command.node.id)
    : dataAnchor
    ? getOutputData(command.node.id, dataAnchor)
    : 'null';

  const changeDataPrefix = () => {
    if (prefix) return dispatch(tabActions.deleteDataPrefix({ node_id: command.node.id }));
    dispatch(flowActions.setShowDataPrefix(command.node.id));
  };

  return (
    <NodeSidebarTab>
      <Stack spacing={1}>
        <Label
          title='Global Prefix'
          tooltip={`Enable this feature to make the data for this node globally available to all other nodes in the workflow.`}
        />
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={Boolean(prefix)}
          exclusive
          onChange={() => changeDataPrefix()}
          fullWidth
        >
          <ToggleButton size='small' value={true}>
            Enabled
          </ToggleButton>
          <ToggleButton size='small' value={false}>
            Disabled
          </ToggleButton>
        </ToggleButtonGroup>
        {prefix && (
          <SubText>
            Prefix Name: <span style={{ color: '#00E3AA' }}>{prefix}</span>
          </SubText>
        )}
      </Stack>
      <Stack spacing={1}>
        <Label
          title='Output Data'
          tooltip={`This is the schema of the data this node outputs. If the node has been executed at least once, real values from the last execution will be used instead of types.`}
        />
        <NodeSidebarCodebox>
          <Code language='json' code={output} />
        </NodeSidebarCodebox>
      </Stack>
    </NodeSidebarTab>
  );
};

export const WebhookData: React.FC<{ command: Command }> = ({ command }) => {
  const dispatch = useDispatch();

  const { getOutputData } = useNodeData();

  const webhookAnchors = command.config.anchors.filter((anchor) => anchor.variant === 'webhook_execution');
  const outputData: { [key: string]: string } = {};

  const dataPrefixes = useSelector((state: State) => state.tabs.active?.flow?.data_prefixes) || {};
  const prefix = dataPrefixes?.[command.node.id];

  for (const anchor of webhookAnchors) {
    const anchorData = getOutputData(command.node.id, anchor);
    outputData[anchor.id] = anchorData;
  }

  const mergedOutput = mergeWebhookObjects(outputData);

  const changeDataPrefix = () => {
    if (prefix) return dispatch(tabActions.deleteDataPrefix({ node_id: command.node.id }));
    dispatch(flowActions.setShowDataPrefix(command.node.id));
  };

  const tooltip = command.output.dynamic
    ? 'This is the payload of your last received webhooks for this workflow.'
    : 'This is the expected output schema of the webhook when received.';

  return (
    <NodeSidebarTab>
      <Stack spacing={1}>
        <Label
          title='Global Prefix'
          tooltip={`Enable this feature to make the data for this node globally available to all other nodes in the workflow.`}
        />
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={Boolean(prefix)}
          exclusive
          onChange={() => changeDataPrefix()}
          fullWidth
        >
          <ToggleButton size='small' value={true}>
            Enabled
          </ToggleButton>
          <ToggleButton size='small' value={false}>
            Disabled
          </ToggleButton>
        </ToggleButtonGroup>
        {prefix && (
          <SubText>
            Prefix Name: <span style={{ color: '#00E3AA' }}>{prefix}</span>
          </SubText>
        )}
      </Stack>
      <Stack spacing={1}>
        <Label title='Webhook Payload' tooltip={tooltip} />
        <NodeSidebarCodebox>
          <Code language='json' code={mergedOutput} />
        </NodeSidebarCodebox>
      </Stack>
    </NodeSidebarTab>
  );
};

/**
 * This is used to merge the payload of all webhooks into a single object
 * @param webhookObj Obj with webhooks separated using a map, one entry for each expected webhook
 * @returns An object that merges all webhooks into a single object
 */

const mergeWebhookObjects = (webhookPayload: { [key: string]: any } | null): any => {
  if (!webhookPayload) return webhookPayload;

  try {
    const consolidated = {};

    for (const [eventName, eventData] of Object.entries(webhookPayload)) {
      const parts = eventName.split('.');
      let current: any = consolidated;

      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }

      const lastPart = parts[parts.length - 1];
      let eventValue = eventData;

      for (const part of parts) {
        if (eventValue && typeof eventValue === 'object' && part in eventValue) {
          eventValue = eventValue[part];
        } else {
          break;
        }
      }

      if (
        eventValue &&
        typeof eventValue === 'object' &&
        Object.keys(eventValue).length === 1 &&
        lastPart in eventValue
      ) {
        eventValue = eventValue[lastPart];
      }

      // Assign the final value
      current[lastPart] = eventValue;
    }

    return consolidated;
  } catch (e) {
    return webhookPayload;
  }
};
